import { useState, useEffect } from 'react'
import { X, Plus } from 'lucide-react'
import { Select } from '../ui/Select'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { useAllCompanies } from '../../hooks/api/useCompanies'
import { useKaseyaGroups } from '../../hooks/api/useKaseyaGroups'
import { useCompanyUsers } from '../../hooks/api/useUsers'
import { AddKaseyaGroupModal } from './AddKaseyaGroupModal'
import { useQueryClient } from '@tanstack/react-query'

interface AddDeviceModalProps {
  isOpen: boolean
  onClose: () => void
  onAddDevice: (deviceData: DeviceFormData) => void
}

export interface DeviceFormData {
  companyId?: string
  kaseyaGroupId: number | null
  deviceName: string
  deviceOwner?: string
  operatingSystem: string
  architecture: string
  deviceType: string
  macAddress: string
  deploymentEmail?: string
  sendAgentNow: boolean
}

const operatingSystems = [
  { value: 'windows', label: 'Windows' },
  { value: 'macos', label: 'macOS' },
  { value: 'linux', label: 'Linux' }
]

const architectures = [
  { value: 'x64', label: 'x64 (64-bit)' },
  { value: 'x86', label: 'x86 (32-bit)' },
  { value: 'arm64', label: 'ARM64' }
]

const deviceTypes = [
  'Workstation',
  'Laptop',
  'Server',
  'Mobile Device',
  'Tablet',
  'Network Device',
  'Printer',
  'Other'
]

export const AddDeviceModal = ({ isOpen, onClose, onAddDevice }: AddDeviceModalProps) => {
  const { user } = useAuth()
  const { data: userRecord } = useUserCheck(user?.id || '')
  const isFullAdmin = userRecord?.admin_level === 'full'
  const queryClient = useQueryClient()

  // State
  const [formData, setFormData] = useState<DeviceFormData>({
    companyId: userRecord?.company_id,
    kaseyaGroupId: null,
    deviceName: '',
    deviceOwner: '',
    operatingSystem: 'windows',
    architecture: 'x64',
    deviceType: 'Workstation',
    macAddress: '',
    deploymentEmail: '',
    sendAgentNow: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAddGroupModal, setShowAddGroupModal] = useState(false)

  // Data fetching
  const { data: companies = [] } = useAllCompanies()
  const { data: kaseyaGroupsData, isLoading: groupsLoading } = useKaseyaGroups(formData.companyId || null)
  const { data: companyUsers = [] } = useCompanyUsers(formData.companyId)

  const kaseyaGroups = kaseyaGroupsData?.groups || []
  const defaultGroupId = kaseyaGroupsData?.defaultGroupId || null

  // Set default company for non-full admins
  useEffect(() => {
    if (!isFullAdmin && userRecord?.company_id) {
      setFormData(prev => ({ ...prev, companyId: userRecord.company_id }))
    }
  }, [isFullAdmin, userRecord?.company_id])

  // Set default Kaseya group when groups load
  useEffect(() => {
    if (kaseyaGroups.length > 0 && !formData.kaseyaGroupId) {
      // Use company's default group if available, otherwise use first group
      const groupToSelect = defaultGroupId
        ? kaseyaGroups.find(g => g.id === defaultGroupId)?.id || kaseyaGroups[0].id
        : kaseyaGroups[0].id

      setFormData(prev => ({ ...prev, kaseyaGroupId: groupToSelect }))
    }
  }, [kaseyaGroups, formData.kaseyaGroupId, defaultGroupId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.kaseyaGroupId) {
      alert('Please select a Kaseya group')
      return
    }

    setIsSubmitting(true)

    try {
      await onAddDevice(formData)
      // Reset form
      setFormData({
        companyId: isFullAdmin ? '' : userRecord?.company_id,
        kaseyaGroupId: null,
        deviceName: '',
        deviceOwner: '',
        operatingSystem: 'windows',
        architecture: 'x64',
        deviceType: 'Workstation',
        macAddress: '',
        deploymentEmail: '',
        sendAgentNow: false
      })
      onClose()
    } catch (error) {
      console.error('Error adding device:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: keyof DeviceFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleCompanyChange = (companyId: string) => {
    setFormData(prev => ({
      ...prev,
      companyId,
      kaseyaGroupId: null, // Reset group when company changes
      deviceOwner: '' // Reset owner when company changes
    }))
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/70"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white flex items-center justify-between p-6 pb-4 border-b border-gray-200">
            <h2 className="text-xl font-medium text-gray-900">Add Device</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Organization (Full Admins Only) */}
            {isFullAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.companyId || ''}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  className="w-full"
                  required
                >
                  <option value="">Select Organization</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {/* Kaseya Group */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kaseya Group <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Select
                  value={formData.kaseyaGroupId?.toString() || ''}
                  onChange={(e) => handleInputChange('kaseyaGroupId', parseInt(e.target.value))}
                  className="flex-1"
                  required
                  disabled={groupsLoading || !formData.companyId}
                >
                  <option value="">
                    {groupsLoading ? 'Loading groups...' : !formData.companyId ? 'Select organization first' : 'Select Kaseya Group'}
                  </option>
                  {kaseyaGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Select>
                <button
                  type="button"
                  onClick={() => setShowAddGroupModal(true)}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Add new group"
                  disabled={!formData.companyId}
                >
                  <Plus className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Device Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Device Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.deviceName}
                onChange={(e) => handleInputChange('deviceName', e.target.value)}
                placeholder="e.g., LAPTOP-JOHN-DOE"
                className="w-full px-3 py-2 border border-gray-300 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Device Owner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Device Owner
              </label>
              <Select
                value={formData.deviceOwner || ''}
                onChange={(e) => handleInputChange('deviceOwner', e.target.value)}
                className="w-full"
                disabled={!formData.companyId}
              >
                <option value="">Unassigned</option>
                {companyUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name || user.email}
                  </option>
                ))}
              </Select>
            </div>

            {/* Operating System */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Operating System <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.operatingSystem}
                onChange={(e) => handleInputChange('operatingSystem', e.target.value)}
                className="w-full"
                required
              >
                {operatingSystems.map((os) => (
                  <option key={os.value} value={os.value}>
                    {os.label}
                  </option>
                ))}
              </Select>
            </div>

            {/* Architecture */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Architecture <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.architecture}
                onChange={(e) => handleInputChange('architecture', e.target.value)}
                className="w-full"
                required
              >
                {architectures.map((arch) => (
                  <option key={arch.value} value={arch.value}>
                    {arch.label}
                  </option>
                ))}
              </Select>
            </div>

            {/* Device Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Device Type <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.deviceType}
                onChange={(e) => handleInputChange('deviceType', e.target.value)}
                className="w-full"
                required
              >
                {deviceTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </div>

            {/* MAC Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                MAC Address
              </label>
              <input
                type="text"
                value={formData.macAddress}
                onChange={(e) => handleInputChange('macAddress', e.target.value)}
                placeholder="00:1B:44:11:3A:B7"
                className="w-full px-3 py-2 border border-gray-300 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                pattern="^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$"
                title="Please enter a valid MAC address (e.g., 00:1B:44:11:3A:B7)"
              />
            </div>

            {/* Deployment Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email for Agent Installer
              </label>
              <input
                type="email"
                value={formData.deploymentEmail || ''}
                onChange={(e) => handleInputChange('deploymentEmail', e.target.value)}
                placeholder="user@company.com"
                className="w-full px-3 py-2 border border-gray-300 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional: Send agent installer link to this email
              </p>
            </div>

            {/* Send Agent Now Checkbox */}
            {formData.deploymentEmail && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="sendAgentNow"
                  checked={formData.sendAgentNow}
                  onChange={(e) => handleInputChange('sendAgentNow', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="sendAgentNow" className="ml-2 text-sm text-gray-700">
                  Send agent installer email immediately
                </label>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting || !formData.kaseyaGroupId}
                className="w-full bg-gray-900 text-white py-2 px-4 rounded-full text-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Adding Device...' : 'Add Device'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* AddKaseyaGroupModal */}
      <AddKaseyaGroupModal
        isOpen={showAddGroupModal}
        onClose={() => setShowAddGroupModal(false)}
        preselectedCompanyId={formData.companyId}
        onGroupAdded={() => {
          // Invalidate Kaseya groups cache to refetch
          queryClient.invalidateQueries({ queryKey: ['kaseya-groups', formData.companyId] })
        }}
      />
    </>
  )
}
